import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from 'cccisd-react-query';
import { client } from 'cccisd-apollo';
import studentQuery from './student.graphql';
import Modal from 'cccisd-modal';
import Table from 'cccisd-table';
import classnames from 'classnames';
import _xor from 'lodash/xor';
import style from './useStudentPicker.css';

const Fortress = window.cccisd.fortress;

const MultipleTable = props => {
    const { columns, data, selected, setSelected, closeModal } = props;
    const [selectedRows, setSelectedRows] = useState(() => selected);

    const checkboxColumn = {
        name: 'checkbox',
        label: 'Name',
        class: 'text-center',
        render: (value, row) => {
            return (
                <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    checked={selectedRows.has(row.pawnId)}
                    onChange={() => {}}
                />
            );
        },
    };

    return (
        <div>
            <div className="mb-3 d-flex gap-2">
                <button
                    type="button"
                    className="btn btn-secondary btn-xs"
                    onClick={() => setSelectedRows(new Set(data.map(item => item.pawnId)))}
                >
                    Select all
                </button>
                <button type="button" className="btn btn-secondary btn-xs" onClick={() => setSelectedRows(new Set())}>
                    Deselect all
                </button>
            </div>
            <Table
                rowKey="pawnId"
                columns={[checkboxColumn, ...columns]}
                data={data}
                showPerPageOptions={false}
                onRowClick={row => {
                    setSelectedRows(new Set(_xor([...selectedRows], [row.pawnId])));
                }}
                showRowClickable
                className="table table-sm table-bordered"
                setRowClass={row => (selectedRows.has(row.pawnId) ? 'table-primary' : '')}
                saveState={false}
                hideShowingRowsSummary
                orderBy="name"
            />
            <div className="mt-4">
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                        closeModal();
                        setSelected(selectedRows);
                    }}
                >
                    Pick {selectedRows.size} student{selectedRows.size !== 1 ? 's' : ''}
                </button>
            </div>
        </div>
    );
};

const Dropdown = props => {
    const { data, selected, setSelected, isMultiple } = props;

    const columns = [
        { name: 'name', label: 'Name', filter: true, sort: true },
        { name: 'studentId', label: 'Student ID', filter: true },
        { name: 'grade', label: 'Grade' },
        { name: 'school', label: 'School', filter: true },
        { name: 'recentAssessmentDate', label: 'Most Recent Assessment', render: value => value.slice(0, 10) },
        { name: 'totalAssessments', label: 'Times Assessed' },
    ];

    const dataObj = useMemo(() => {
        return (data || []).reduce((obj, item) => {
            obj[item.pawnId] = item;
            return obj;
        }, {});
    }, [data]);

    if (!data) {
        return null;
    }

    const renderMultipleTrigger = () => {
        const value = (() => {
            const existingSelected = [...selected].filter(id => id in dataObj);
            const total = existingSelected.length;
            if (total === 0) {
                return '';
            }
            if (total === 1) {
                return dataObj[existingSelected[0]].name;
            }
            return `${total} students`;
        })();

        return (
            <input
                name="students"
                type="text"
                className={classnames('form-select form-select-sm', style.input)}
                placeholder="Choose students..."
                value={value}
                onChange={() => {}}
            />
        );
    };

    const renderSingleTrigger = () => {
        const selectedStudent = dataObj[[...selected][0]];

        return (
            <input
                name="student"
                type="text"
                className={classnames('form-select form-select-sm', style.input)}
                placeholder="Choose a Student"
                value={selectedStudent?.name || ''}
                onChange={() => {}}
            />
        );
    };

    const renderSingleTable = ({ closeModal }) => {
        return (
            <Table
                rowKey="pawnId"
                columns={columns}
                data={data}
                showPerPageOptions={false}
                onRowClick={row => {
                    setSelected(new Set([row.pawnId]));
                    closeModal();
                }}
                showRowClickable
                className="table table-sm table-bordered"
                setRowClass={row => {
                    return selected.has(row.pawnId) ? 'table-primary' : '';
                }}
                saveState={false}
                hideShowingRowsSummary
                orderBy="name"
            />
        );
    };

    return (
        <div className="d-flex align-items-center gap-3">
            <label className="form-label mb-0">Student:</label>
            <Modal
                trigger={isMultiple ? renderMultipleTrigger() : renderSingleTrigger()}
                title="Pick a student"
                size="large"
                render={
                    isMultiple
                        ? ({ closeModal }) => (
                              <MultipleTable
                                  columns={columns}
                                  data={data}
                                  selected={selected}
                                  setSelected={setSelected}
                                  closeModal={closeModal}
                              />
                          )
                        : renderSingleTable
                }
            />
        </div>
    );
};

const useStudentPicker = ({ initialValue, isMultiple = false } = {}) => {
    const [selected, setSelected] = useState(() => {
        if (!initialValue) {
            return new Set();
        }
        if (Array.isArray(initialValue)) {
            return new Set(initialValue);
        }

        return new Set([initialValue]);
    });

    const { data } = useQuery({
        queryKey: ['getProviderStudents', Fortress.user.acting.id],
        queryFn: async () => {
            const res = await client.query({
                query: studentQuery,
                variables: { instructorPawnId: Fortress.user.acting.id },
                fetchPolicy: 'network-only',
            });

            const list = res.data.roles.learnerList.map(item => ({
                pawnId: item.pawn.pawnId,
                studentId: item.fields.studentId,
                name: `${item.fields.firstName} ${item.fields.lastName}`,
                grade: item.fields.grade,
                school: item.groupName,
                totalAssessments: item.selectedAssignmentPlanList.length,
                recentAssessmentDate:
                    item.selectedAssignmentPlanList.length === 0
                        ? '-'
                        : item.selectedAssignmentPlanList.reduce((date, plan) => {
                              if (plan.createdAt > date) return plan.createdAt;
                              return date;
                          }, ''),
                reporters: item.childRoles.contactList.reduce(
                    (obj, role) => {
                        obj[role.fields.contactType] ||= [];
                        obj[role.fields.contactType].push({ pawnId: role.pawn.pawnId, name: role.fields.contactName });
                        return obj;
                    },
                    {
                        Student: [
                            { pawnId: item.pawn.pawnId, name: `${item.fields.firstName} ${item.fields.lastName}` },
                        ],
                    }
                ),
            }));

            return list;
        },
    });

    useEffect(() => {
        if (!data) {
            return;
        }

        if (data.length === 1) {
            const { pawnId } = data[0];
            if (!selected.has(pawnId) || selected.size !== 1) {
                setSelected(new Set([pawnId]));
            }
        } else {
            const pawnIds = new Set(data.map(item => item.pawnId));
            const existingSelected = [...selected].filter(id => pawnIds.has(id));
            if (selected.size !== existingSelected.length) {
                setSelected(new Set(existingSelected));
            }
        }
    }, [data, selected]);

    const selectedStudents = useMemo(() => {
        if (isMultiple) {
            return data ? data.filter(item => selected.has(item.pawnId)) : [];
        }
        return data?.find(item => selected.has(item.pawnId));
    }, [data, selected, isMultiple]);

    const renderField = () => (
        <Dropdown data={data} selected={selected} setSelected={setSelected} isMultiple={isMultiple} />
    );

    return {
        loading: !data,
        selected: selectedStudents,
        total: data?.length,
        renderField,
    };
};

export default useStudentPicker;
